<template>
  <div class="cupom">
    <div class="filters">
      <SearchInput
        v-model="searchTerm"
        :suggestions="searchInputSuggestions"
        class="filters--search_width"
        @keypress.enter="filterByText"
        @selectItem="searchSelectedItemHandler"
      />

      <!-- FILTROS -->
      <div class="dropdowns-filters">
        <!-- STATUS -->
        <FilterDropdown
          v-model="statusFilterValue"
          :options="statusFilterOptions"
        ></FilterDropdown>

        <!-- TARIFAS -->
        <FilterDropdownCoupon
          v-model="ratesFilterValue"
          :options="ratesOptions"
          :radioOptions="false"
          :hasSearch="true"
          :fieldsToSearch="['label']"
          searchFieldPlaceholder="Selecionar tarifa"
          label="Tarifas"
        ></FilterDropdownCoupon>

        <!-- AGRUPADOR -->
        <FilterDropdown
          v-if="lots"
          v-model="grouperFilterValue"
          :options="grouperFilterOptions"
          :radioOptions="false"
          :hasSearch="true"
          :fieldsToSearch="['label']"
          searchFieldPlaceholder="Selecionar agrupador"
          label="Agrupador"
        ></FilterDropdown>
      </div>
    </div>

    <!-- TABELA -->
    <Datatable
      :cols="gridColumns"
      :rows="gridData"
      :filterKey="filterKey"
      :noFrames="true"
      :selectableRow="true"
      :sortable="false"
      :selectedsRowsTerms="'cupom selecionado | cupons selecionados'"
      :searchableColumns="['cupom']"
      :isLoading="isLoadingTableContent"
      class="datatable"
      ref="datatable"
      notFoundMessage="Não há cupons correspondentes com a busca."
      @getSelectedRows="manageSelectedCupons"
      @rowInteraction="saveRowInteraction"
    >
      <template v-slot:valor="{ col, rowDataObj }">
        <span v-if="+rowDataObj.tipoValor === 3">{{
          col | pluralizer('noite | {count} noites')
        }}</span>
        <span v-else-if="+rowDataObj.tipoValor === 1"
          >{{ col | numFormat('0.0') }} %</span
        >
        <span v-else>R$ {{ col | numFormat('0.0,[00]') }}</span>
      </template>

      <template v-slot:ativo="{ col }">
        <span v-if="col">Ativo</span>
        <span v-else>Inativo</span>
      </template>

      <!-- SE FOR DO TIPO "LIMITE POR PESSOA" NÃO DEVE APRESENTAR A QUANTIDADE -->
      <template v-slot:quantidadeDisponivel="{ col, rowDataObj }">
        <span v-if="rowDataObj.tipoLimiteUso === 4">-</span>
        <span v-else>{{ col ?? '-' }}</span>
      </template>

      <template v-slot:quantidade="{ col }">
        <span v-if="col < 0">Sem limite</span>
        <span
          v-else
          :class="{
            'datatable--quantity-column__danger-color': col === 0
          }"
          >{{ col | numFormat }}</span
        >
      </template>

      <template v-slot:acoes="slotProps">
        <Dropdown class="datatable-actions" position="bottom-end">
          <template #button-content>
            <Icon icon="dots" />
          </template>
          <!-- Editar em lot -->
          <DropdownItem
            v-if="lots"
            :id="slotProps.rowDataObj.codigoAgrupador"
            :to="
              `/cupons-desconto/editar/${slotProps.rowDataObj.codigoAgrupador}?tipo=2`
            "
            >Editar</DropdownItem
          >
          <!-- Editar cupom único -->
          <DropdownItem
            v-else
            :id="slotProps.rowDataObj.codigo"
            :to="
              `/cupons-desconto/editar/${slotProps.rowDataObj.codigo}?tipo=1`
            "
            >Editar</DropdownItem
          >

          <!-- Inativar cupom único -->
          <DropdownItem
            v-if="slotProps.rowDataObj.ativo && !lots"
            :id="slotProps.rowDataObj.codigo"
            element="button"
            class="danger-color"
            @click.stop.prevent="
              excludeSingleCoupon(slotProps.rowDataObj.codigo)
            "
          >
            Inativar
          </DropdownItem>

          <!-- Inativar Em massa -->
          <DropdownItem
            v-if="slotProps.rowDataObj.ativo && lots"
            :id="slotProps.rowDataObj.codigoAgrupador"
            element="button"
            class="danger-color"
            @click.stop.prevent="
              excludeSingleGroup(slotProps.rowDataObj.codigoAgrupador)
            "
          >
            Inativar
          </DropdownItem>
        </Dropdown>
      </template>
    </Datatable>

    <PmwPagination
      :itemsPerPage="50"
      :itemsPerPageOptions="itemsPerPageOptions"
      :totalItems="listTotalItems"
      :currentPage="paginationCurrentPage"
      class="datatable--pagination"
      @changePagination="filterCupons"
    />

    <AlertModal
      v-model="showAlertModal"
      text="Inativando o cupom, ele não estará mais disponível para ser editado e usado pelos clientes. Deseja continuar? "
      @alert-modal-confirm="excludeCoupons()"
      @alert-modal-cancel="cancelExcludeCoupons"
    />

    <!-- MODAL DE CONFIRMAÇAÕ DE INATIvAÇÃO DE AGRUPADORES DE CUPONS -->
    <AlertModal v-model="showFeedbackModal" :buttonClose="true">
      <template #text>
        A desativação dos cupons pode demorar<br />
        alguns minutos para ser processada.
        <br />
        Você pode continuar navegando tranquilamente <br />
        que te avisaremos assim que finalizar.
      </template>
      <template #actions>
        <Button size="large" @click="showFeedbackModal = false">
          Ok, continuar
        </Button>
      </template>
    </AlertModal>
  </div>
</template>

<script>
import debounce from 'lodash.debounce'
import SearchInput from '@/components/_base/elements/SearchInput'
import FilterDropdown from '@/components/_base/patterns/FilterDropdown'
import FilterDropdownCoupon from '@/components/_base/patterns/FilterDropdownCoupon'
import Datatable from '@/components/_base/patterns/DataTable'
import AlertModal from '@/components/_base/patterns/AlertModal'
import Dropdown from '@/components/_base/patterns/Dropdown'
import DropdownItem from '@/components/_base/patterns/DropdownItem'
import PmwPagination from '@/components/_base/patterns/PmwPagination'
import Button from '@/components/_base/elements/Button'

export default {
  name: 'CouponsList',
  components: {
    SearchInput,
    FilterDropdown,
    FilterDropdownCoupon,
    Datatable,
    AlertModal,
    Dropdown,
    DropdownItem,
    PmwPagination,
    Button
  },
  props: {
    /** Controla se é listagem de cupons em lots */
    lots: {
      type: Boolean,
      default: false
    },

    /** Controla se a listagem de cupons em lots é cupom único ou não */
    singleLots: {
      type: Boolean,
      default: false
    },

    /** Array com os últimos cupons excluidos */
    lastExcludedCoupons: {
      type: Array,
      default: () => []
    },

    /** Array dos cupons selecionados */
    selectedCupons: {
      type: Array,
      default: () => []
    },

    /** Opções de tarifas */
    ratesOptions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      showAlertModal: false,
      isLoadingTableContent: true,
      isSingleCoupon: false,
      singleCoupon: null,
      singleGroup: null /** Aramazena o grupo que deve ser inativado */,
      pageTitle: 'Cupons de desconto',
      searchTerm: '',
      searchTermSelected: '',
      statusFilterOptions: [
        {
          label: 'Todos',
          value: 2,
          checked: false
        },
        {
          label: 'Ativos',
          value: 1,
          checked: true
        },
        {
          label: 'Inativos',
          value: 0,
          checked: false
        }
      ],
      statusFilterValue: {
        label: 'Ativos',
        value: 1,
        checked: true
      },
      ratesFilterValue: [],
      grouperFilterValue: [],
      grouperFilterOptions: [],
      gridData: [],
      gridColumns: [
        /** colunas do grid */
        { name: 'cupom', alias: 'Cupom' },
        { name: 'valor', alias: 'Valor' },
        { name: 'ativo', alias: 'Status' },
        { name: 'quantidadeDisponivel', alias: 'Quant. Disponível' },
        { name: 'acoes', alias: '' }
      ],
      searchInputSuggestions: [],
      itemsPerPageOptions: [
        {
          text: '50',
          value: 50
        }
      ] /** Opções do total de itens apresentados na listagem */,
      listTotalItems: 0 /** Total de itens da listagem */,
      paginationCurrentPage: 1 /** Página atual da paginação */,
      showFeedbackModal: false /** Modal de confirmação de inativação */
    }
  },
  computed: {
    filterKey() {
      if (this.searchTerm.trim() !== '') return this.searchTermSelected

      return ''
    },
    filterValues() {
      return {
        status: this.statusFilterValue.value,
        rates: this.ratesFilterValue.map((item) => item.value).join(','),
        grouper: this.grouperFilterValue.map((item) => item.value).join(',')
      }
    },
    hasActiveSelectedCupons() {
      let hasActive = false

      for (let i = 0, len = this.selectedCupons.length; i < len; i++) {
        const cupom = this.selectedCupons[i]

        /** Se tem um ativo interrompe o loop */
        if (cupom.ativo) {
          hasActive = true
          break
        }
      }

      return hasActive
    }
  },
  watch: {
    searchTerm(newValue) {
      if (newValue === '') {
        this.searchTermSelected = newValue
        this.filterCupons()
        this.paginationCurrentPage = 1
      }
    },
    filterValues: {
      handler: debounce(function(newVal, oldVal) {
        if (
          newVal &&
          (newVal.status !== oldVal.status ||
            newVal.rates !== oldVal.rates ||
            newVal.grouper !== oldVal.grouper)
        ) {
          this.filterCupons()
          this.paginationCurrentPage = 1
        }
      }, 1000)
    },
    lastExcludedCoupons: {
      handler(newValue) {
        if (newValue.length > 0) {
          const excludedArr = !this.lots
            ? newValue
            : newValue.map((item) => item.codigoAgrupador)
          this.excludeCoupons(excludedArr)
        }
      },
      deep: true
    },
    /** Limpa os cupons marcados na tabela */
    selectedCupons: {
      handler(newVal) {
        if (newVal.length === 0) {
          this.gridData.forEach((item) => (item.data.checked = false))
        }
      },
      deep: true
    }
  },
  created() {
    /** Colunas da listagem dos cupons em massa */
    if (this.lots) {
      this.gridColumns = [
        { name: 'cupom', alias: 'Cupom' },
        { name: 'agrupador', alias: 'Agrupador' },
        { name: 'valor', alias: 'Valor' },
        { name: 'ativo', alias: 'Status' },
        { name: 'quantidadeDisponivel', alias: 'Quant. Disponível' },
        { name: 'acoes', alias: '' }
      ]
    }

    /** Cria as opções de filtro dos agrupadores */
    this.$coupon.filterGroupers().then((response) => {
      response.data.forEach((item) => {
        const obj = {}

        obj.label = item.nome
        obj.value = item.codigo

        this.grouperFilterOptions.push(obj)
      })
    })

    this.filterCupons()

    const localStorageItemName = `${location.host}_coupon_search`
    const localStorageSugg = localStorage.getItem(localStorageItemName)

    this.searchInputSuggestions = JSON.parse(localStorageSugg) || []
  },
  mounted() {
    /** Cria um atributo não reativo */
    this.datatableHead = this.$refs['datatable'].$el.querySelector('.thead')
    /** Adiciona a escuta do evento de scroll */
    document.addEventListener('scroll', this.scrollHandler)
  },
  beforeDestroy() {
    document.removeEventListener('scroll', this.scrollHandler)
  },
  methods: {
    manageSelectedCupons(values) {
      let selectedCoupons = []

      selectedCoupons = values

      /** Envia os dados de checked para o componente pai */
      this.$emit('input', selectedCoupons)
      this.$emit('change', selectedCoupons)
    },
    /**
     * Salva a interação com o item da tabela em localstorage
     */
    saveRowInteraction(event) {
      if (
        this.searchTermSelected &&
        event.cupom.toLowerCase().indexOf(this.searchTermSelected) > -1
      ) {
        const localStorageItemName = `${location.host}_coupon_search`
        const localStorageSugg = localStorage.getItem(localStorageItemName)

        const itemObj = {
          text: event.cupom,
          link: `/cupons-desconto/editar/${event.codigo}`
        }

        if (!localStorageSugg) {
          localStorage.setItem(localStorageItemName, JSON.stringify([itemObj]))
        } else {
          const localStorageSuggArray = JSON.parse(localStorageSugg)
          const foundItem = localStorageSuggArray.find(
            (item) => item.text === event.cupom
          )

          if (!foundItem) {
            localStorageSuggArray.push(itemObj)
          }

          this.searchInputSuggestions = localStorageSuggArray

          localStorage.setItem(
            localStorageItemName,
            JSON.stringify(this.searchInputSuggestions)
          )
        }
      }
    },
    searchSelectedItemHandler(value) {
      this.$router.push({
        path: value.item.link
      })
    },
    excludeSingleCoupon(codigo) {
      this.showAlertModal = true
      this.isSingleCoupon = true
      this.singleCoupon = [codigo]
    },
    excludeSingleGroup(codigo) {
      this.showAlertModal = true
      this.singleGroup = [codigo]
    },
    cancelExcludeCoupons() {
      this.isSingleCoupon = false
      this.singleCoupon = null
    },
    excludeCoupons(excludeds) {
      this.showAlertModal = false

      if (this.lots && !this.singleLots) {
        const reqData = excludeds || this.singleGroup

        /** Modal de notificação de inativação */
        this.showFeedbackModal = true

        this.$coupon
          .deleteLots(reqData)
          .then((response) => {
            if (response.status === 200) {
              /** Mensagem toast de inativação */
              this.$bus.$emit('show-alert-chip', {
                message: 'Desativando agrupadores de cupons.'
              })

              this.gridData = this.gridData.filter(
                (gridItem) =>
                  reqData.findIndex(
                    (excludedItem) =>
                      excludedItem === gridItem.data.codigoAgrupador
                  ) < 0
              )

              this.$emit('couponsDeleted')
            }
          })
          .catch((error) => {
            this.$bus.$emit('show-alert-chip', {
              message: error.response.data[0].message
            })
          })
      } else {
        const couponsArray = this.isSingleCoupon
          ? this.singleCoupon
          : this.selectedCupons.map((item) => item.codigo)

        this.$coupon
          .delete(couponsArray)
          .then((response) => {
            if (response.status === 200) {
              this.$bus.$emit('show-alert-chip', {
                message: this.$options.filters.pluralizer(
                  couponsArray.length,
                  'Cupom desativado com sucesso | Cupons desativados com sucesso'
                )
              })

              /** exclui do array da tabela */
              couponsArray.forEach((codigo) => {
                const idx = this.gridData.findIndex(
                  (item) => item.data.codigo === codigo
                )

                if (idx > -1) {
                  this.gridData.splice(idx, 1)
                }
              })

              this.$emit('couponsDeleted')
            }
          })
          .catch((error) => {
            this.$bus.$emit('show-alert-chip', {
              message: error.response.data[0].message
            })
          })
      }

      this.isSingleCoupon = false
      this.singleCoupon = null
    },

    /** Filtra ao inserir um texto na busca */
    filterByText($event) {
      this.searchTermSelected = $event.target.value
      this.filterCupons()
    },

    /**
     * Filtra os cupons de acordo com os filtros selecionados
     * todo: otimizar para não fazer requisições em cada troca de tab
     */
    filterCupons(paginationOptions = null) {
      const filterObj = {}

      this.isLoadingTableContent = true

      if (this.statusFilterValue.value !== 2) {
        filterObj.ativo = this.statusFilterValue.value > 0
      }

      /** Se existe paginação adiciona na busca */
      if (paginationOptions) {
        filterObj.pagina = paginationOptions.currentPage
        this.paginationCurrentPage = paginationOptions.currentPage
      }

      /** Se selecionar todas as tarifas, envia um array vazio para não derrubar o server */
      filterObj.tarifas =
        this.ratesFilterValue.length ===
        this.ratesOptions.flatMap((item) => item.subfields).length
          ? []
          : this.ratesFilterValue.map((item) => item.value)

      filterObj.agrupadores = this.grouperFilterValue.map((item) => item.value)

      if (this.searchTerm) {
        filterObj.textoCupons = this.searchTerm
      }

      if (this.lots) {
        this.$coupon
          .filterLots(filterObj)
          .then((response) => {
            /**
             * TODO: Ver com o Renato para criar um método que verifica se o hotel possui agrupadores
             */

            /** Armazena o total de registros */
            this.listTotalItems = response.data.totalRegistros

            this.gridData = this.gridData = this.standardizeCouponsLotsData(
              response
            )

            this.isLoadingTableContent = false
          })
          .catch((error) => {
            this.$bus.$emit('show-alert-chip', {
              message: error.response.data[0].message
            })
          })
      } else {
        this.$coupon
          .filter(filterObj)
          .then((response) => {
            this.gridData = this.gridData = this.standardizeCouponData(response)

            /** Armazena o total de registros */
            this.listTotalItems = response.data.totalRegistros

            this.isLoadingTableContent = false
          })
          .catch((error) => {
            this.$bus.$emit('show-alert-chip', {
              message: error.response.data[0].message
            })
          })
      }

      /** Limpa os cupons selecionados */
      this.$emit('input', [])
      this.$emit('change', [])
    },
    /** Retorna cupons únicos */
    standardizeCouponData(response) {
      return response.data.cuponsDesconto.map((item) => {
        const obj = {
          data: item
        }

        return obj
      })
    },
    /** Retorna cupons em massa */
    standardizeCouponsLotsData(response) {
      return response.data.cuponsDesconto.map((item) => {
        const obj = {
          data: item
        }

        return obj
      })
    },
    /** gerencia o scroll na página */
    scrollHandler() {
      /** Adiciona a classe de estilo no header da tabela quando fixa no topo */
      if (this.datatableHead.getBoundingClientRect().y === 50) {
        if (!this.datatableHead.classList.contains('scrolled')) {
          this.datatableHead.classList.add('scrolled')
        }
      } else {
        if (this.datatableHead.classList.contains('scrolled')) {
          this.datatableHead.classList.remove('scrolled')
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.cupom {
  .info-panel {
    &--main-text {
      width: 100%;
      margin-bottom: 10px;
      line-height: 1;
      font-size: font-size('size-xx-large');
      font-weight: font-weight('weight-medium');

      &__smaller {
        font-size: font-size('size-large');
      }
    }

    &--aux-text {
      margin-bottom: 10px;
      font-size: font-size('size-large');
      font-weight: font-weight('weight-light');
    }
  }
}
/**
* Espacamento padrao
* Ref: https://www.figma.com/file/9AFe6i7bVhMup1bYeppCU7/Tarifas?node-id=0%3A1
*/
.filters {
  padding: {
    bottom: 30px;
  }

  &--search_width {
    width: 214px;
  }

  .dropdowns-filters {
    margin-top: 16px;
  }
}
/**
* Espacamento padrao
* Ref: https://www.figma.com/file/9AFe6i7bVhMup1bYeppCU7/Tarifas?node-id=0%3A1
*/
.datatable {
  max-height: initial;
  overflow: initial;

  &--pagination {
    position: sticky;
    bottom: 0;
    z-index: 1;
  }

  ::v-deep {
    .thead {
      top: 50px;

      &:before,
      &:after {
        content: '';
        width: 50px;
        height: 100%;
        position: absolute;
        top: 0;
        background-color: $white;
        right: 100%;
      }

      &::after {
        right: initial;
        left: 100%;
      }
    }
    .dropdown {
      &-item {
        &:hover,
        &:active {
          background-color: $gray-lightest;
        }
      }
      .danger-color {
        &.dropdown-item {
          color: $red-2;
        }
      }

      &--trigger-button {
        color: $gray-mid;
      }
      &--options {
        min-width: 100px;
      }
    }

    &--quantity-column__danger-color {
      color: $red-2;
    }
  }
}
</style>
